/**
 * Physical-drive delivery wording, shared by the operator Physical
 * Drives page, DCP Distribute, and DCP Distribution (v12.0.0).
 *
 * Pre-v12 each page rendered the raw DB status ("staged", "requested")
 * or its own paraphrase ("Staged — awaiting ship"), and none of them
 * said the drive was safe to eject or that the KDM was on it. Operator
 * shipped a drive not knowing either. One vocabulary, one place.
 *
 * DB status values are unchanged; this module only maps them to words.
 * No dashes in any label (house copy rule).
 */

// Lifecycle as the operator experiences it. received / tested are
// timestamps on the row rather than statuses, so effectiveStage()
// folds them in.
export const DRIVE_STAGES = [
  { key: 'requested', label: 'Requested' },
  { key: 'staging', label: 'Copying' },
  { key: 'staged', label: 'Ready to ship' },
  { key: 'shipped', label: 'Shipped' },
  { key: 'delivered', label: 'Delivered' },
  { key: 'received', label: 'Received' },
  { key: 'tested', label: 'Tested' },
];

const STAGE_INDEX = Object.fromEntries(DRIVE_STAGES.map((s, i) => [s.key, i]));

export function effectiveStage(d) {
  if (!d) return null;
  const s = d.status === 'pending' ? 'requested' : d.status;
  if (d.tested_at && ['delivered', 'returned', 'shipped'].includes(s)) return 'tested';
  if (d.received_at && ['delivered', 'returned', 'shipped'].includes(s)) return 'received';
  return s;
}

/** Index into DRIVE_STAGES, or -1 for off-path states (failed, cancelled, returned, cleared). */
export function stageIndex(d) {
  const k = effectiveStage(d);
  return k in STAGE_INDEX ? STAGE_INDEX[k] : -1;
}

/**
 * What's known about the KDM for this drive.
 * key: on_drive | missing | pending | not_included | none | not_copied
 */
export function kdmState(d) {
  if (!d) return { key: 'none', label: 'No KDM', color: 'default' };
  if (d.include_kdm === false) {
    return { key: 'not_included', label: 'KDM not included', color: 'default',
             tooltip: 'This drive was set to ship without the KDM. Send the key separately from Distribution.' };
  }
  if (!d.kdm_record_id) {
    return { key: 'none', label: 'No KDM', color: 'default',
             tooltip: 'No KDM is linked to this delivery. Unencrypted DCPs do not need one.' };
  }
  if (d.kdm_staged_at) {
    return { key: 'on_drive', label: 'KDM on drive', color: 'success',
             tooltip: 'The KDM was copied onto the drive next to the DCP, saved as .kdm so the server finds it.' };
  }
  if (['requested', 'pending', 'staging'].includes(d.status)) {
    return { key: 'pending', label: 'KDM pending', color: 'info',
             tooltip: 'The KDM is copied onto the drive after the DCP finishes.' };
  }
  if (['failed', 'cancelled'].includes(d.status)) {
    return { key: 'not_copied', label: 'KDM not copied', color: 'default' };
  }
  return { key: 'missing', label: 'KDM missing', color: 'error',
           tooltip: 'The KDM did not reach the drive. Send it from Distribution, or copy it on before shipping.' };
}

/**
 * Status chip for a delivery. { key, label, color, tooltip }.
 * `label` is the short form; the Distribute pages use driveSummaryLabel().
 */
export function physicalStatus(d) {
  const stage = effectiveStage(d);
  const pct = Math.round(d?.progress_pct || 0);
  const kdm = kdmState(d);
  switch (stage) {
    case 'requested':
      return { key: stage, label: 'Requested', color: 'warning',
               tooltip: 'Waiting for a drive. Plug in and mount a drive, then Assign + Stage.' };
    case 'staging':
      return { key: stage, label: pct > 0 ? `Copying to drive ${pct}%` : 'Copying to drive', color: 'info',
               tooltip: 'Copying the DCP onto the drive. Leave it plugged in.' };
    case 'staged':
      return { key: stage, label: 'Ready to ship', color: 'success',
               tooltip: kdm.key === 'on_drive'
                 ? 'DCP and KDM copied and verified. Eject the drive, then mark it shipped.'
                 : kdm.key === 'missing'
                   ? 'DCP copied and verified, but the KDM is missing. Eject the drive, then mark it shipped.'
                   : 'DCP copied and verified. Eject the drive, then mark it shipped.' };
    case 'shipped':
      return { key: stage, label: 'Shipped', color: 'primary' };
    case 'delivered':
      return { key: stage, label: 'Delivered', color: 'success' };
    case 'received':
      return { key: stage, label: 'Received by venue', color: 'success' };
    case 'tested':
      return { key: stage, label: 'Tested at venue', color: 'success' };
    case 'returned':
      return { key: stage, label: 'Returned', color: 'default' };
    case 'cleared':
      return { key: stage, label: 'Drive cleared', color: 'default' };
    case 'failed':
      return { key: stage, label: 'Copy failed', color: 'error', tooltip: d?.error_message || undefined };
    case 'cancelled':
      return { key: stage, label: 'Cancelled', color: 'default' };
    default:
      return { key: stage || 'unknown', label: stage || 'Unknown', color: 'default' };
  }
}

/** What's on the drive, e.g. "DCP + KDM", "DCP only". */
export function driveContents(d) {
  const k = kdmState(d).key;
  if (k === 'on_drive') return 'DCP + KDM';
  if (k === 'missing') return 'DCP only, KDM missing';
  return 'DCP only';
}

/**
 * Longer label for the DCP Distribute / Distribution pages, where the
 * chip stands alone without a KDM column next to it.
 */
export function driveSummaryLabel(d) {
  const st = physicalStatus(d);
  if (st.key === 'staged') return `Drive ready to ship (${driveContents(d)})`;
  if (st.key === 'staging') {
    const pct = Math.round(d?.progress_pct || 0);
    return pct > 0 ? `Drive copying ${pct}%` : 'Drive copying';
  }
  if (st.key === 'requested') return 'Drive requested';
  if (st.key === 'failed') return 'Drive copy failed';
  return `Drive ${st.label.charAt(0).toLowerCase()}${st.label.slice(1)}`;
}

/**
 * The single next thing the operator should do on the Physical Drives
 * page. `driveMounted` is the mount state of the slot this delivery's
 * drive sits in (true / false), or null when unknown (different node
 * selected, status not loaded).
 *
 * key: assign | wait | eject | ship | mark_delivered | venue | done | failed
 */
export function nextStep(d, { driveMounted = null } = {}) {
  const stage = effectiveStage(d);
  switch (stage) {
    case 'requested':
      return { key: 'assign', label: 'Assign + Stage',
               hint: 'Plug in a drive, mount it, then start the copy.' };
    case 'staging':
      return { key: 'wait', label: null,
               hint: 'Copying. Leave the drive plugged in.' };
    case 'staged':
      if (driveMounted !== false) {
        return { key: 'eject', label: 'Eject drive',
                 hint: 'Eject before unplugging. Shipping unlocks once the drive is ejected.' };
      }
      return { key: 'ship', label: 'Mark shipped',
               hint: 'Drive ejected and safe to unplug. Pack it and record the shipment.' };
    case 'shipped':
      return { key: 'mark_delivered', label: 'Mark delivered',
               hint: 'Record the carrier signature when it arrives.' };
    case 'delivered':
      return { key: 'venue', label: null,
               hint: 'Waiting on the venue. Received and Tested are marked on the Distribution page.' };
    case 'received':
      return { key: 'venue', label: null,
               hint: 'Waiting on the venue test. Marked on the Distribution page.' };
    case 'failed':
      return { key: 'failed', label: null,
               hint: d?.error_message ? `Copy failed: ${d.error_message}` : 'Copy failed.' };
    default:
      return { key: 'done', label: null, hint: null };
  }
}

/** Rows that belong in the "needs something from me" card list. */
export function isActiveDelivery(d) {
  return ['requested', 'pending', 'staging', 'staged', 'shipped', 'failed'].includes(d?.status);
}
